import { useEffect, useMemo, useReducer } from 'react';
import { useDebounce } from '../../../hooks/useDebounce';
import type { ActiveFilter, SortConfig } from './types';

interface DataGridState {
  page: number;
  pageSize: number;
  sort?: SortConfig;
  search: string;
  activeFilters: ActiveFilter[];
  selectedRows: Set<string>;
  autoRefreshInterval: number;
}

interface DataGridStateOptions {
  initialPage?: number;
  initialPageSize?: number;
  initialSort?: SortConfig;
  initialSearch?: string;
  initialFilters?: ActiveFilter[];
  initialSelectedRows?: Set<string>;
  initialAutoRefreshInterval?: number;
  syncToUrl?: boolean;
  urlPrefix?: string;
}

type DataGridAction =
  | { type: 'SET_PAGE'; payload: number }
  | { type: 'SET_PAGE_SIZE'; payload: number }
  | { type: 'SET_SORT'; payload?: SortConfig }
  | { type: 'SET_SEARCH'; payload: string }
  | { type: 'SET_FILTERS'; payload: ActiveFilter[] }
  | { type: 'SET_SELECTED_ROWS'; payload: Set<string> }
  | { type: 'TOGGLE_ROW'; payload: { rowId: string; checked: boolean } }
  | { type: 'CLEAR_SELECTION' }
  | { type: 'SET_AUTO_REFRESH'; payload: number }
  | { type: 'RESET'; payload: DataGridState };

function createDefaultState(options: DataGridStateOptions): DataGridState {
  return {
    page: options.initialPage ?? 1,
    pageSize: options.initialPageSize ?? 25,
    sort: options.initialSort,
    search: options.initialSearch ?? '',
    activeFilters: options.initialFilters ?? [],
    selectedRows: options.initialSelectedRows ?? new Set<string>(),
    autoRefreshInterval: options.initialAutoRefreshInterval ?? 0,
  };
}

function parseStateFromUrl(options: DataGridStateOptions): DataGridState | null {
  if (!options.syncToUrl || typeof window === 'undefined') return null;

  const defaults = createDefaultState(options);
  const params = new URLSearchParams(window.location.search);
  const prefix = options.urlPrefix ?? 'grid';

  const page = Number(params.get(`${prefix}Page`) ?? defaults.page);
  const pageSize = Number(params.get(`${prefix}PageSize`) ?? defaults.pageSize);
  const search = params.get(`${prefix}Search`) ?? defaults.search;
  const sortRaw = params.get(`${prefix}Sort`);
  const filtersRaw = params.get(`${prefix}Filters`);
  const autoRefreshInterval = Number(params.get(`${prefix}AutoRefresh`) ?? defaults.autoRefreshInterval);

  const sort = sortRaw
    ? (() => {
        const [field, direction] = sortRaw.split(':');
        if (!field || (direction !== 'asc' && direction !== 'desc')) return undefined;
        return { field, direction: direction as 'asc' | 'desc' };
      })()
    : defaults.sort;

  let activeFilters = defaults.activeFilters;
  if (filtersRaw) {
    try {
      const parsed = JSON.parse(filtersRaw);
      if (Array.isArray(parsed)) {
        activeFilters = parsed.filter((entry): entry is ActiveFilter => {
          if (!entry || typeof entry !== 'object') return false;
          const candidate = entry as Record<string, unknown>;
          return typeof candidate.id === 'string' && 'value' in candidate;
        });
      }
    } catch {
      activeFilters = defaults.activeFilters;
    }
  }

  return {
    page: Number.isFinite(page) && page > 0 ? page : defaults.page,
    pageSize: Number.isFinite(pageSize) && pageSize > 0 ? pageSize : defaults.pageSize,
    sort,
    search,
    activeFilters,
    selectedRows: defaults.selectedRows,
    autoRefreshInterval:
      Number.isFinite(autoRefreshInterval) && autoRefreshInterval >= 0
        ? autoRefreshInterval
        : defaults.autoRefreshInterval,
  };
}

function dataGridReducer(state: DataGridState, action: DataGridAction): DataGridState {
  switch (action.type) {
    case 'SET_PAGE':
      return { ...state, page: Math.max(1, action.payload) };
    case 'SET_PAGE_SIZE':
      return { ...state, pageSize: Math.max(1, action.payload), page: 1 };
    case 'SET_SORT':
      return {
        ...state,
        page: 1,
        sort: action.payload?.field ? action.payload : undefined,
      };
    case 'SET_SEARCH':
      return { ...state, search: action.payload, page: 1 };
    case 'SET_FILTERS':
      return { ...state, activeFilters: action.payload, page: 1 };
    case 'SET_SELECTED_ROWS':
      return { ...state, selectedRows: action.payload };
    case 'TOGGLE_ROW': {
      const nextSelectedRows = new Set(state.selectedRows);
      if (action.payload.checked) nextSelectedRows.add(action.payload.rowId);
      else nextSelectedRows.delete(action.payload.rowId);
      return { ...state, selectedRows: nextSelectedRows };
    }
    case 'CLEAR_SELECTION':
      return { ...state, selectedRows: new Set<string>() };
    case 'SET_AUTO_REFRESH':
      return { ...state, autoRefreshInterval: Math.max(0, action.payload) };
    case 'RESET':
      return action.payload;
    default:
      return state;
  }
}

export function useDataGridState(options: DataGridStateOptions = {}) {
  const initialState = parseStateFromUrl(options) ?? createDefaultState(options);
  const [state, dispatch] = useReducer(dataGridReducer, initialState);
  const debouncedSearch = useDebounce(state.search, 300);

  useEffect(() => {
    if (!options.syncToUrl || typeof window === 'undefined') return;

    const prefix = options.urlPrefix ?? 'grid';
    const params = new URLSearchParams(window.location.search);

    params.set(`${prefix}Page`, String(state.page));
    params.set(`${prefix}PageSize`, String(state.pageSize));

    if (state.search) params.set(`${prefix}Search`, state.search);
    else params.delete(`${prefix}Search`);

    if (state.sort?.field) {
      params.set(`${prefix}Sort`, `${state.sort.field}:${state.sort.direction}`);
    } else {
      params.delete(`${prefix}Sort`);
    }

    if (state.activeFilters.length > 0) {
      params.set(`${prefix}Filters`, JSON.stringify(state.activeFilters));
    } else {
      params.delete(`${prefix}Filters`);
    }

    if (state.autoRefreshInterval > 0) {
      params.set(`${prefix}AutoRefresh`, String(state.autoRefreshInterval));
    } else {
      params.delete(`${prefix}AutoRefresh`);
    }

    const query = params.toString();
    const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
    window.history.replaceState(null, '', nextUrl);
  }, [
    options.syncToUrl,
    options.urlPrefix,
    state.activeFilters,
    state.autoRefreshInterval,
    state.page,
    state.pageSize,
    state.search,
    state.sort,
  ]);

  const actions = useMemo(
    () => ({
      setPage: (page: number) => dispatch({ type: 'SET_PAGE', payload: page }),
      setPageSize: (pageSize: number) => dispatch({ type: 'SET_PAGE_SIZE', payload: pageSize }),
      setSort: (sort?: SortConfig) => dispatch({ type: 'SET_SORT', payload: sort }),
      setSearch: (search: string) => dispatch({ type: 'SET_SEARCH', payload: search }),
      setFilters: (activeFilters: ActiveFilter[]) => dispatch({ type: 'SET_FILTERS', payload: activeFilters }),
      setSelectedRows: (selectedRows: Set<string>) => dispatch({ type: 'SET_SELECTED_ROWS', payload: selectedRows }),
      toggleRow: (rowId: string, checked: boolean) => dispatch({ type: 'TOGGLE_ROW', payload: { rowId, checked } }),
      clearSelection: () => dispatch({ type: 'CLEAR_SELECTION' }),
      setAutoRefreshInterval: (interval: number) => dispatch({ type: 'SET_AUTO_REFRESH', payload: interval }),
      reset: () => dispatch({ type: 'RESET', payload: createDefaultState(options) }),
    }),
    [options],
  );

  return {
    state: {
      ...state,
      debouncedSearch,
    },
    actions,
  };
}
